'use client';

import { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { TWorkPageType } from '@/types/global';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { ProjectFilters } from '@/components/organisms/ProjectFilters/ProjectFilters';
import { ProjectsList } from './ProjectsList/ProjectsList';
import { ProjectsLoader } from './ProjectsLoader/ProjectsLoader';
import { usePageStore } from '@/store/usePageStore';
import { ArchivedProjectsGallery } from '@/components/organisms/FilteredProjectsGallery/ArchivedProjectsGallery/ArchivedProjectsGallery';
import { NoProjectsFound } from './NoProjectsFound/NoProjectsFound';
import {
  IFilteredProjectsGallery,
  IProjectFilteredList,
} from './FilteredProjectsGallery.types';
import { useFiltersStore } from '@/store/useFiltersStore';
import { useInitialFilteredProjectsFetch } from './hooks/useInitialFilteredProjectsFetch';
import { useLazyLoadProjects } from './hooks/useLazyLoadProjects';
import { fetchProjectsClientCached } from '@/directus/services/common/projects/fetch-client';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { fetchImagesWithProjects } from '@/directus/services/common/images-with-projects/fetch';
import { computeParamsFetch } from '@/utils/filters';
import { ImagesWithProjectsList } from './ImagesList/ImagesWithProjectsList';
import { localizedSlugs } from '@/i18n/routing';
import { usePathname } from 'next/navigation';
import { scrollToRef } from '@/utils/generic';
import * as Styled from './FilteredProjectsGallery.styled';
import { useInitialOrRestoreProjects } from './hooks/useInitialOrRestoreProjects';
import { isWorkPage } from '@/utils/page';

gsap.registerPlugin(ScrollTrigger);

/**
 * Displays a list of projects as a result of a set of filters
 * ⚠️⚠️ Recommended to use Redux devtools to debug the state of useFiltersStore ⚠️⚠️
 * https://chromewebstore.google.com/detail/redux-devtools/lmhkpmbekcpmknklioeibfkpmmfibljd
 **/
export function FilteredProjectsGallery({
  width = 'main',
  isProject = false,
  filters,
  initialProjList,
  initialProjectsIDs,
  hiddenFiltersIds = [],
  isPrivatePresentation = false,
  isSpecificWorkPage = false,
  pageFilters = [],
  bufferedProjects = [],
}: IFilteredProjectsGallery): JSX.Element {
  // Zustand useFiltersStore:
  const search = useFiltersStore((s) => s.search);
  const isProjectsLazyLoadFinished = useFiltersStore((s) => s.isProjectsLazyLoadFinished);
  const selectedFilters = useFiltersStore((s) => s.selectedFilters);
  const setArchivedFilteredProjects = useFiltersStore((s) => s.setArchivedFilteredProjects);
  const archivedFilteredProjects = useFiltersStore((s) => s.archivedFilteredProjects);
  const imageSearch = useFiltersStore((s) => s.imageSearch);
  const imagesWithProjects = useFiltersStore((s) => s.imagesWithProjects);

  // Zustand usePageStore:
  const isUserLogged = usePageStore((s) => s.isUserLogged);
  const langIso = usePageStore((s) => s.langIso);
  const locale = usePageStore((s) => s.locale);

  // Router info early (used by helpers)
  const pathName = usePathname();

  const projectSlugPrefix = useMemo(
    () => [`/${localizedSlugs.projects.en}`, `/${localizedSlugs.projects.es}`],
    []
  );

  const memoizedInitialProjList = useMemo(() => initialProjList, [initialProjList, locale]);

  const workPageType = useRef<TWorkPageType>(
    isSpecificWorkPage ? 'work-specific' : isPrivatePresentation ? 'work-private' : 'work'
  );

  const refProjectsList = useRef<HTMLDivElement>(null);
  const refProjectsContainer = useRef<HTMLDivElement>(null);
  const refLoaderInitial = useRef<HTMLDivElement>(null);
  const refLoaderArchivedProjects = useRef<HTMLDivElement>(null);
  const refHasHydrated = useRef(false);

  // Component state
  const [firstFetchProjects, setFirstFetchProjects] = useState<IProjectFilteredList[]>(memoizedInitialProjList);
  const [lazyLoadedProjectsLists, setLazyLoadedProjectsLists] = useState<IProjectFilteredList[]>([]);

  const useHasHydrated = () => {
    const [hasHydrated, setHasHydrated] = useState(false);
    useEffect(() => {
      // useFiltersStore.getState().resetFiltersAndProjects();
      setHasHydrated(true);
      refHasHydrated.current = true;
      fadeInProjectsList();
    }, []);
    return hasHydrated;
  };
  const hasHydrated = useHasHydrated();
  

  const fadeInLoaderInitial = useCallback((duration?: number) => {
    const el = refLoaderInitial.current;
    if (el) gsap.to(el, { opacity: 0.65, duration: duration || 0.3, ease: 'sine.out' });
  }, []);

  const fadeOutLoaderInitial = useCallback((duration?: number) => {
    const el = refLoaderInitial.current;
    if (el) gsap.to(el, { opacity: 0, duration: duration || 0.3, ease: 'sine.out' });
  }, []);

  const fadeOutProjectsList = useCallback(() => {
    if (refProjectsContainer.current) refProjectsContainer.current.style.opacity = '0';
  }, []);

  const fadeInProjectsList = useCallback(() => {
    if (refProjectsContainer.current) refProjectsContainer.current.style.opacity = '1';
  }, []);

  // when change locale, projects lists might be cached in previous language, so reset
  useEffect(() => {
    useFiltersStore.getState().resetFiltersAndProjects();
  }, [locale]);


  /**
   * Updates the initial fetch projects with the projects from the store.
   * if there are not projects, it sets the initial projects from the memoized initial project list.
   */
  const updateInitialFetchDisplayedProjects = useCallback(() => {
    const firstFetchedProjects = useFiltersStore.getState().firstFetchFilteredProjects;
    if (firstFetchedProjects) {
      setFirstFetchProjects(firstFetchedProjects);
    } else {
      useFiltersStore.getState().setFirstFetchFilteredProjects(memoizedInitialProjList);
      setFirstFetchProjects(memoizedInitialProjList.map((p) => ({ ...p })));
    }
    fadeOutLoaderInitial(0);
  }, [memoizedInitialProjList, fadeOutLoaderInitial]);

  /**
   * Updates the lazy loaded projects lists from the store
   */
  const updateLazyLoadedProjectsLists = useCallback(() => {
    const currentLazyLoaded = useFiltersStore.getState().lazyLoadedProjectsLists;
    setLazyLoadedProjectsLists(currentLazyLoaded ?? []);
  }, [setLazyLoadedProjectsLists]);

  /**
   * Determines if the previous state should be restored based on the current path and filters.
   */
  const shouldRestoreState = useCallback(() => {
    const prevPageState = useFiltersStore.getState().savedFilteredProjectsState;
    const isSameWorkPage = !!(prevPageState?.path && prevPageState.path === pathName);
    const filtersSelection = useFiltersStore.getState().selectedFilters?.filters || [];
    const hadActiveFilters =
      (filtersSelection && filtersSelection.length > 0) ||
      Boolean(prevPageState?.search?.value) ||
      Boolean(prevPageState?.imageSearch?.value);
    const previousVisitedPath = usePageStore.getState().previousVisitedPath;
    const isPreviousPageProject = projectSlugPrefix.some((prefix) =>
      (previousVisitedPath || '').startsWith(prefix)
    );
    const hadFirstFetchFilteredProjects = !!(
      prevPageState?.firstFetchFilteredProjects && prevPageState?.firstFetchFilteredProjects?.length > 0
    );
    const hadLazyLoadedProjectsLists = !!(
      prevPageState?.lazyLoadedProjectsLists && prevPageState?.lazyLoadedProjectsLists?.length > 0
    );
    const hadNextBufferLazyLoadedProjects = !!(
      prevPageState?.nextBufferLazyLoadedProjects && prevPageState?.nextBufferLazyLoadedProjects?.length > 0
    );

    return (
      isSameWorkPage &&
      isPreviousPageProject &&
      (hadActiveFilters || hadFirstFetchFilteredProjects || hadLazyLoadedProjectsLists || hadNextBufferLazyLoadedProjects)
    );
  }, [pathName, projectSlugPrefix]);

  useEffect(() => {
    if (!refHasHydrated.current) return;

    fadeInProjectsList();
  }, [firstFetchProjects, fadeInProjectsList]);

  const { fetchInitialFilteredProjects } = useInitialFilteredProjectsFetch({
    pageFilters,
    workPageType: workPageType.current,
    langIso,
    isUserLogged,
    initialProjectsIDs,
    updateInitialFetchDisplayedProjects,
    updateLazyLoadedProjectsLists,
    fadeInProjectsList,
    fadeOutProjectsList,
    fadeInLoaderInitial,
    fadeOutLoaderInitial,
  });

  /**
   * Used in when there is a search input and the user is logged in.
   * The result sets the ImagesList component instead of the ProjectsList component.
   */
  const fetchImagesWithProjectsList = useCallback(async () => {
    if (!imageSearch?.value) return;
    const paramsToFetch = computeParamsFetch(pageFilters);
    useFiltersStore.setState({ isCurrentlyFetchingProjects: true });
    const imgWithProjectsResp = await fetchImagesWithProjects({ ...paramsToFetch, keyword: imageSearch?.value });
    useFiltersStore.getState().setImagesWithProjects(imgWithProjectsResp?.projects || []);
    useFiltersStore.setState({ isCurrentlyFetchingProjects: false });
  }, [imageSearch, pageFilters]);


  const fetchProjectsOrLoadInitialList = useCallback(()=> {
    useFiltersStore.getState().setIsProjectsLazyLoadFinished({
      isFinished: false,
      triggerer: 'initialFetch',
    });
    const imageSearchValue = useFiltersStore.getState().imageSearch?.value;
    const searchValue = useFiltersStore.getState().search?.value;
    // usePageStore.setState({ shouldDisplayFooter: false });

    if (imageSearchValue && isUserLogged) {
      fetchImagesWithProjectsList();
      return;
    }

    if ((selectedFilters && selectedFilters.filters?.length > 0) || searchValue) {
      fetchInitialFilteredProjects();
      return;
    }

    // No selected filters → reset from server props
    useFiltersStore.setState({
      nextBufferLazyLoadedProjects: bufferedProjects,
      isProjectsLazyLoadFinished: {
        isFinished: false,
        triggerer: 'resetInitial',
      },
      paginationLazyLoad: 2,
      firstFetchFilteredProjects: undefined,
      lazyLoadedProjectsLists: [],
    });

    const elFiltersContainer = document.getElementById('filters-container');
    scrollToRef(elFiltersContainer);

    fadeOutProjectsList();
    fadeOutLoaderInitial(0);

    const timeout = setTimeout(() => {
      setFirstFetchProjects(memoizedInitialProjList);
      setLazyLoadedProjectsLists([]);
      useFiltersStore.setState({
        projectsIDs: initialProjectsIDs,
        nextBufferLazyLoadedProjects: bufferedProjects,
        isProjectsLazyLoadFinished: {
          isFinished: false,
          triggerer: 'initialFetch',
        },
      });
      fadeInProjectsList();
    }, 100);

    return () => clearTimeout(timeout);
  }, [
    fetchInitialFilteredProjects,
    fetchImagesWithProjectsList,
    isUserLogged,
    memoizedInitialProjList,
    initialProjectsIDs,
    bufferedProjects,
    selectedFilters,
    fadeOutProjectsList,
    fadeOutLoaderInitial,
    fadeInProjectsList
  ]);

  // Initial load / restore from previous navigation
  useInitialOrRestoreProjects({
    hasHydrated,
    isPrivatePresentation,
    memoizedInitialProjList,
    bufferedProjects,
    initialProjectsIDs,
    filtersData: filters,
    fadeOutProjectsList,
    fadeInProjectsList,
    updateInitialFetchDisplayedProjects,
    fetchProjectsOrLoadInitialList,
    setFirstFetchProjects,
    setLazyLoadedProjectsLists,
    shouldRestoreState,
  });

  // When filters change, fetch initial batch of projects or load initial list
  useEffect(() => {
    if (!refHasHydrated.current) return;

    if (
      !selectedFilters?.filters ||
      selectedFilters?.triggerer === 'resetInitial' ||
      selectedFilters?.triggerer === 'restore'
    ) {
      return;
    }

    fetchProjectsOrLoadInitialList();
    // do not add more dependencies, only selectedFilters is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  useEffect(() => {
    if (!refHasHydrated.current) return;
    if (!search?.triggerer || search?.triggerer === 'resetInitial' || search?.triggerer === 'restore') return;

    if (search?.triggerer === 'restore-only-filters' && search.value && isWorkPage(usePageStore.getState().previousVisitedPath || '')) {
      fetchProjectsOrLoadInitialList();
    } else if (search?.triggerer === 'ui') {
      fetchProjectsOrLoadInitialList();
    }

    // do not add more dependencies, trigger effect only when search value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);


  // When image search value changes, fetch images with projects or load initial list
  useEffect(() => {
    if (!refHasHydrated.current) return;

    if (!imageSearch?.value || !isUserLogged || imageSearch?.triggerer === 'resetInitial' || imageSearch?.triggerer === 'restore') return;

    if (!imageSearch.value) {
      fetchProjectsOrLoadInitialList();
    } else {
      fetchImagesWithProjectsList();
    }
  // do not add more dependencies, trigger effect only when imageSearch value changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSearch]);
  
  const { loadMoreRef } = useLazyLoadProjects({
    langIso,
    search,
    selectedFilters,
    updateLazyLoadedProjectsLists,
  });


  /** 
   * Saves the current state of the filtered projects when navigating away
   */
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const handler = () => {
      useFiltersStore.getState().saveFilteredProjectsState(pathName);
      // useFiltersStore.getState().resetFiltersAndProjects();
    };

    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      if (
        a.origin === location.origin &&
        a.target !== '_blank' &&
        e.button === 0 &&
        !e.defaultPrevented &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.shiftKey &&
        !e.altKey
      ) {
        handler();
      }
    };

    const onPopState = () => handler();

    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPopState);
    };
  }, [pathName]);

  /** 
   * Fetch archived projects from IDs when lazy load is finished and there are filters selected 
   */
  useEffect(() => {
    if (workPageType.current === 'work' && (selectedFilters?.filters && selectedFilters.filters.length === 0 && !search?.value)) {
      setArchivedFilteredProjects([]);
      useFiltersStore.getState().setIsArchivedProjFetchFinished(true);
      return;
    }
    const fetchArchivedProjectsFromIDs = async () => {
      const archivedProjectsIDs = useFiltersStore.getState().projectsIDs?.archived;
      const isArchivedProjFetchFinished = useFiltersStore.getState().isArchivedProjFetchFinished;

      if (!isProjectsLazyLoadFinished?.isFinished && isProjectsLazyLoadFinished?.triggerer === 'resetInitial') return;

      if ((!archivedProjectsIDs || archivedProjectsIDs?.length === 0) && !isArchivedProjFetchFinished) {
        setArchivedFilteredProjects([]);
        useFiltersStore.getState().setIsArchivedProjFetchFinished(true);
        return;
      }
      if (isProjectsLazyLoadFinished.isFinished) {
        if (archivedProjectsIDs?.length === 0) {
          setArchivedFilteredProjects([]);
          useFiltersStore.getState().setIsArchivedProjFetchFinished(true);
          return;
        }

        const archivedProjects = await fetchProjectsClientCached({
          onlyIDs: archivedProjectsIDs || [],
          lang: langIso,
          count: 80
        });

        setArchivedFilteredProjects(archivedProjects?.projects || []);
        useFiltersStore.getState().setIsArchivedProjFetchFinished(true);
      }
    };  

    fetchArchivedProjectsFromIDs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectsLazyLoadFinished]);

  /**
   * Mutation observer to refresh ScrollSmoother when projects list changes
   * This is necessary to ensure that the smoother is aware of new elements added to the DOM so it avoids scroll jumps
   */
  useEffect(() => {
    const el = refProjectsList.current;
    if (!el) return;

    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        const smoother = ScrollSmoother.get();
        if (smoother) {
          smoother.refresh();
        }
      });
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [refProjectsList]);

  const areArchivedProjectsRendered = useMemo(() => {
    const firstFetchFilteredProjects = useFiltersStore.getState().firstFetchFilteredProjects;

    return firstFetchFilteredProjects?.length === 1;
  }, []);

  return (
    <>
      <ComponentContainer
        width={width}
        marginBottom="sm"
        isProject={isProject}
        className={isPrivatePresentation ? 'filtered-projects-gallery-private' : ''}
      >
        <ProjectFilters
          filtersData={filters}
          hiddenFiltersIds={hiddenFiltersIds}
          isPrivatePresentation={isPrivatePresentation}
          workPageType={workPageType.current}
          pageFilters={pageFilters}
        />

        <Styled.ProjectsListsWrapper>
          <Styled.ProjectsListsContainer ref={refProjectsContainer}>
            {/* Images list, only active when input of image search is not empty */}
            {imageSearch?.value && imagesWithProjects && <ImagesWithProjectsList />}

            {/* Initial project lists (from page load or filters) */}
            {!imageSearch?.value &&
              Array.isArray(firstFetchProjects) &&
              firstFetchProjects.map((projectList, i) => {
                if (projectList && projectList.projects?.length > 0) {
                  const numProjectsToShow =
                    (firstFetchProjects?.length ?? 0) > 1
                      ? 4
                      : !isPrivatePresentation
                        ? 12
                        : 500;

                  return (
                    <ProjectsList
                      key={`project-list-${i}`}
                      projectList={projectList}
                      numProjectsToShow={numProjectsToShow}
                      isPrivatePresentation={isPrivatePresentation}
                      isSpecificWorkPage={isSpecificWorkPage}
                      ref={refProjectsList as React.RefObject<HTMLDivElement>}
                    />
                  );
                }

                if (firstFetchProjects?.length === 1) {
                  return <NoProjectsFound key={`no-projects-${i}`} />;
                }
                return null;
              })}

            {/* Lazy loaded projects list, loaded when scrolling */}
            {!imageSearch?.value &&
              lazyLoadedProjectsLists?.map((projectList, i) => (
                <ProjectsList
                  key={`lazy-project-list-${i}`}
                  projectList={projectList}
                  numProjectsToShow={12}
                  isPrivatePresentation={isPrivatePresentation}
                  isAdditionalList={true}
                  className="lazy-loaded-projects-list"
                />
              ))}

            {/* Load more trigger for lazy load */}
            <div data-load-more id="load-more-ref" ref={loadMoreRef} />

            {/* Loader to show when loading lazy loaded projects */}
            {(firstFetchProjects?.length === 1 &&
              !isProjectsLazyLoadFinished &&
              !isPrivatePresentation &&
              !imageSearch?.value) && (
                <ProjectsLoader
                  numProjectsToShow={4}
                  className="loader-additional-list"
                />
              )}
          </Styled.ProjectsListsContainer>

          {/* Loader for initial list, shows when is first fetching */}
          <ProjectsLoader
            numProjectsToShow={8}
            className="loader-initial-list"
            ref={refLoaderInitial}
          />
        </Styled.ProjectsListsWrapper>
      </ComponentContainer>

      {/* Archived projects gallery */}
      {(
        (
          workPageType.current === 'work' && (selectedFilters?.filters?.length > 0 || search?.value) ||
          // In work specific there is already a first filtered projects list so no need to check for filters
          workPageType.current === 'work-specific'
        ) &&
        isProjectsLazyLoadFinished &&
        archivedFilteredProjects && archivedFilteredProjects?.length > 0 
        // !imageSearch?.value
      ) && (
        <ArchivedProjectsGallery projects={archivedFilteredProjects} />
      )}

      {/* Archived projects loader */}
      {(areArchivedProjectsRendered &&
        isProjectsLazyLoadFinished &&
        !archivedFilteredProjects &&
        !imageSearch?.value) && (
          <Styled.ArchivedProjectsLoaderContainer>
            <ComponentContainer>
              <ProjectsLoader
                numProjectsToShow={6}
                className="loader-archived-additional-list"
                ref={refLoaderArchivedProjects}
              />
            </ComponentContainer>
          </Styled.ArchivedProjectsLoaderContainer>
        )}
    </>
  );
}